import React, { useEffect, useState } from "react";
import { INDUSTRY_BENCHMARKS, FormInputs } from "./roas_types";
// import "./roas.css";
import { Box } from "./roasStyle";
import { isInIframe } from "../../utils/is-in-iframe";
import { ToolsHeader } from "../components/ToolsHeader";

const RoasCalculator: React.FC = () => {
  const [form, setForm] = useState<Partial<FormInputs>>({});
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    ratio: number;
    status: string;
    recommendations: string[];
  } | null>(null);

  useEffect(() => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  }, [result]);
  const getStatusText = (ratio: number) => {
    if (ratio < 2) return "Low";
    if (ratio < 3) return "Average";
    return "Excellent";
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value === "" ? undefined : isNaN(+value) ? value : +value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const {
      totalRevenue,
      adSpend,
      industry,
      conversionRate,
      costPerLead,
      customerLifetimeValue,
      customerAcquisitionCost,
    } = form;

    if (totalRevenue == null || adSpend == null || adSpend === 0 || !industry) {
      setError(
        !totalRevenue || !adSpend
          ? "Please fill in all required fields"
          : "Ad spend cannot be zero"
      );
      return;
    }

    const ratio = totalRevenue / adSpend;
    const status = getStatusText(ratio);
    const industryData = INDUSTRY_BENCHMARKS[industry];
    const recommendations = [...industryData.recommendations];

    if (ratio < 2) {
      recommendations.push(
        "Focus on ad creative and messaging to improve engagement",
        "Reallocate budget to high-performing channels",
        "Improve landing page optimization"
      );
    } else if (ratio < 3) {
      recommendations.push(
        "Test new audiences or expand geographies",
        "Explore A/B testing for ad creatives",
        "Increase customer lifetime value through retention campaigns"
      );
    } else {
      recommendations.push(
        "Scale the campaign by increasing budget",
        "Diversify into additional ad platforms",
        "Double down on top-performing creatives"
      );
    }

    setResult({ ratio, status, recommendations });
    setError(null);
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  };

  return (
    <Box>
      <div
        className={`container container_roas ${
                  !isInIframe ? "py-5 defaultText" : "py-1 interDisplay"
                }`}
                style={{padding:"0"}}
      >
        {!isInIframe && (
          <ToolsHeader
            title="ROAS Calculator"
            description="Calculate and optimize your Return on Ad Spend (ROAS) quickly
                and efficiently."
          />
        )}
        {/* <div className="header">
          <h1>ROAS Calculator</h1>
          <p>Calculate and optimize your Return on Ad Spend</p>
        </div> */}

        <form onSubmit={handleSubmit} id="roasForm" className="calculator-form">
          <div className="form-grid">
            <div className="form-section">
              <div className="form-group-roas">
                <label htmlFor="totalRevenue">Total Revenue ($) *</label>
                <input
                  type="number"
                  name="totalRevenue"
                  id="totalRevenue"
                  required
                  min="0"
                  step="0.01"
                  onChange={handleChange}
                />
              </div>
              <div className="form-group-roas">
                <label htmlFor="adSpend">Ad Spend ($) *</label>
                <input
                  type="number"
                  id="adSpend"
                  name="adSpend"
                  required
                  min="0"
                  step="0.01"
                  onChange={handleChange}
                />
              </div>
              <div className="form-group-roas">
                <label htmlFor="industry">Industry *</label>
                <select
                  name="industry"
                  id="industry"
                  onChange={handleChange}
                  required
                >
                  <option value="">Select</option>
                  {Object.entries(INDUSTRY_BENCHMARKS).map(([key, val]) => (
                    <option key={key} value={key}>
                      {val.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group-roas">
                <label htmlFor="conversionRate">Conversion Rate (%)</label>
                <input
                  type="number"
                  id="conversionRate"
                  name="conversionRate"
                  min="0"
                  max="100"
                  step="0.01"
                  onChange={handleChange}
                />
              </div>
              <div className="form-group-roas">
                <label htmlFor="costPerLead">Cost Per Lead ($)</label>
                <input
                  type="number"
                  id="costPerLead"
                  name="costPerLead"
                  min="0"
                  step="0.01"
                  onChange={handleChange}
                />
              </div>
              <div className="form-group-roas">
                <label htmlFor="customerLifetimeValue">
                  Customer Lifetime Value ($)
                </label>
                <input
                  type="number"
                  id="customerLifetimeValue"
                  name="customerLifetimeValue"
                  min="0"
                  step="0.01"
                  onChange={handleChange}
                />
              </div>
              <div className="form-group-roas form-group-full">
                <label htmlFor="customerAcquisitionCost">
                  Customer Acquisition Cost ($)
                </label>
                <input
                  type="number"
                  id="customerAcquisitionCost"
                  name="customerAcquisitionCost"
                  min="0"
                  step="0.01"
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
          {error && (
            <div id="error" className="error-message">
              <svg
                className="error-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              {error}
            </div>
          )}

          <button type="submit" className="submit-button">
            Calculate ROAS
          </button>
        </form>

        {result && form.industry && (
          <div className="results" id="results">
            <div
              className={`results-grid ${
                result.status === "Low"
                  ? "results-grid-low"
                  : result.status === "Average"
                  ? "results-grid-average"
                  : "results-grid-excellent"
              }`}
            >
              <div className="results-header">
                <h2>Results</h2>
                <svg
                  className="trend-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M23 6l-9.5 9.5-5-5L1 18"></path>
                  <path d="M17 6h6v6"></path>
                </svg>
              </div>

              <div className="metrics-grid">
                <div className="metric-card">
                  <div className="metric-header">
                    <h3>Your ROAS</h3>
                    <span className="status-icon">
                      {" "}
                      <svg
                        className="trend-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path d="M23 6l-9.5 9.5-5-5L1 18"></path>
                        <path d="M17 6h6v6"></path>
                      </svg>
                    </span>
                  </div>
                  <p
                    className={`metric-value ${
                      result.status === "Low"
                        ? "metric-value-red"
                        : result.status === "Average"
                        ? "metric-value-yellow"
                        : "metric-value-green"
                    }`}
                    id="roasValue"
                  >
                    {result.ratio.toFixed(2)}:1
                  </p>
                  <p className="metric-status">
                    Status: <span id="roasStatus">{result.status}</span>
                  </p>
                </div>
                <div className="metric-card">
                  <div className="metric-header">
                    <h3>Industry Benchmark</h3>
                    <span className="status-icon">
                      {" "}
                      <svg
                        className="target-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <circle cx="12" cy="12" r="10"></circle>
                        <circle cx="12" cy="12" r="6"></circle>
                        <circle cx="12" cy="12" r="2"></circle>
                      </svg>
                    </span>
                  </div>
                  <p className="metric-value" id="benchmarkValue">
                    {INDUSTRY_BENCHMARKS[form.industry].min}:1 -{" "}
                    {INDUSTRY_BENCHMARKS[form.industry].max}:1
                  </p>
                  <p className="metric-status" id="industryName">
                    {INDUSTRY_BENCHMARKS[form.industry].name}
                  </p>
                </div>
                <div className="metric-card">
                  <div className="metric-header">
                    <h3>Key Metrics</h3>
                    <span className="status-icon">
                      {" "}
                      <svg
                        className="chart-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                        <line x1="8" y1="18" x2="8" y2="12"></line>
                        <line x1="12" y1="18" x2="12" y2="8"></line>
                        <line x1="16" y1="18" x2="16" y2="6"></line>
                      </svg>
                    </span>
                  </div>
                  <div className="key-metrics">
                    <p>
                      Revenue:{" "}
                      <span id="revenueValue">
                        {formatCurrency(form.totalRevenue || 0)}
                      </span>
                    </p>
                    <p>
                      Ad Spend:{" "}
                      <span id="spendValue">
                        {formatCurrency(form.adSpend || 0)}
                      </span>
                    </p>
                    {form.conversionRate != null && (
                      <p id="conversionRateValue">
                        Conv. Rate: <span>{form.conversionRate}%</span>
                      </p>
                    )}
                  </div>
                  {/* <p className="metric-value metric-value-green" id="roasValue">
                  {result.ratio.toFixed(2)}:1
                </p>
                <p className="metric-status">
                  Status: <span id="roasStatus">{result.status}</span>
                </p> */}
                </div>
              </div>
            </div>
            <div className="recommendations">
              <div className="recommendations-header">
                <h2>Recommendations</h2>
                <span>Based on your performance</span>
              </div>
              <div id="recommendationsList" className="recommendations-grid">
                {result.recommendations.map((rec, i) => (
                  <div className="recommendation-item">
                    <span className="recommendation-number">{i + 1}</span>
                    <span className="recommendation-text">{rec}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Box>
  );
};

export default RoasCalculator;
